/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, ReactNode, PropsWithChildren } from 'react';

import ReactInfiniteScroll, { Props } from 'react-infinite-scroll-component';

import {
  useIsInsideContainer,
  useShadowRootElement,
  useSearchSdkOutsideParentNode,
} from '../hooks';

import { useStyles } from './InfiniteScroll.styles';
import { InfiniteScrollContext } from './InfiniteScrollProvider';

/**
 * Свойства компонента {@link InfiniteScroll}
 */
export interface InfiniteScrollProps extends Props {
  /**
   * id элемента, который будет использоваться как контейнер прокрутки.
   */
  scrollId?: string;
  /**
   * будет осуществлен поиск первой родительской scroll ноды для SDK виджета снаружи контейнера.
   */
  searchSdkScrollOutsideNode?: boolean;

  /**
   * Если true, используется специальный идентификатор для прокрутки внутри диалога.
   *
   * @defaultValue `false`
   */
  scrollableDialog?: boolean;

  /**
   * Если true, применяет стили для переполнения к компоненту.
   *
   * @defaultValue `false`
   */
  withOverflow?: boolean;

  /**
   * Если true, переполнение становится видимым.
   */
  overflowVisible?: boolean;

  /**
   * Радиус скругления для обрезки контента.
   */
  clipRadius?: number;

  /**
   * Флаг, указывающий, что данные в данный момент загружаются.
   */
  isLoading?: boolean;

  /**
   * Флаг, указывающий, есть ли ещё данные для загрузки.
   */
  hasMore: boolean;

  /**
   * Флаг, отключающий проверку высоты контейнера.
   */
  disableContainerHeightCheck?: boolean;

  /**
   * Функция для загрузки следующей порции данных.
   */
  next: () => void;
}

const SCROLLABLE_DIALOG_ID = 'scrollable-dialog';

/**
 * Пользовательский компонент InfiniteScroll, который расширяет `react-infinite-scroll-component`.
 * Он позволяет при необходимости применять стили для переполнения и использует контекст для определения целевого элемента прокрутки.
 *
 * @public
 * @param scrollId - id элемента, который будет использоваться как контейнер прокрутки.
 * @param scrollableDialog - Если true, используется специальный идентификатор для прокрутки внутри диалога.
 * @param withOverflow - Если true, применяет стили для переполнения к компоненту.
 * @param overflowVisible - Если true, переполнение становится видимым.
 * @param disableContainerHeightCheck - Флаг, отключающий проверку высоты контейнера.
 * @param clipRadius - Радиус скругления для обрезки контента.
 * @param isLoading - Флаг, указывающий, что данные в данный момент загружаются.
 * @param hasMore - Флаг, указывающий, есть ли ещё данные для загрузки.
 * @param next - Функция для загрузки следующей порции данных.
 * @param children - Содержимое, которое будет отображено внутри компонента InfiniteScroll.
 * @param props - Остальные свойства из `react-infinite-scroll-component`.
 * @param searchSdkScrollOutsideNode - будет осуществлен поиск первой родительской scroll ноды для SDK виджета снаружи контейнера.
 * @returns  JSX-элемент, представляющий компонент InfiniteScroll.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link InfiniteScrollProps}
 */
export const InfiniteScroll = ({
  children,
  scrollableDialog = false,
  scrollId,
  disableContainerHeightCheck,
  searchSdkScrollOutsideNode,
  clipRadius,
  overflowVisible,
  ...props
}: PropsWithChildren<InfiniteScrollProps>) => {
  const { classes, cx } = useStyles({ clipRadius });
  const { withOverflow } = props;
  const scrollContext = useContext(InfiniteScrollContext);
  const scrollTarget = scrollableDialog ? SCROLLABLE_DIALOG_ID : scrollContext;
  const isInsideContainer = useIsInsideContainer();

  const scrollWidgetId = useShadowRootElement(scrollId);

  const parentScrollSdkNode = useSearchSdkOutsideParentNode();

  let finalScrollTarget: string | Element | null;

  if (!isInsideContainer) {
    finalScrollTarget = scrollId ?? scrollTarget;
  } else if (searchSdkScrollOutsideNode) {
    finalScrollTarget = parentScrollSdkNode ?? scrollWidgetId;
  } else {
    finalScrollTarget = scrollWidgetId;
  }

  // Ключ нужен для принудительного ремаунта компонента,
  // когда scrollableTarget меняется со строки/null на HTMLElement.
  // Без этого ReactInfiniteScroll продолжает использовать старый контейнер,
  // так как библиотека не обновляет scrollableTarget после изменения компонента, а только на маунте
  const scrollableKey =
    typeof finalScrollTarget === 'string' || !finalScrollTarget
      ? 'string-target'
      : 'element-target';

  useEffect(() => {
    if (!props.hasMore || props.isLoading || disableContainerHeightCheck) {
      return;
    }

    const container: Element | null =
      typeof finalScrollTarget === 'string'
        ? document.getElementById(finalScrollTarget)
        : finalScrollTarget;

    if (!container) {
      return;
    }

    const hasScrollbar = container.scrollHeight > container.clientHeight;

    if (!hasScrollbar) {
      props.next();
    }
  }, [
    props.dataLength,
    props.hasMore,
    props.isLoading,
    disableContainerHeightCheck,
  ]);

  const className = cx(
    withOverflow && classes.infiniteScroll,
    !!clipRadius && classes.clip,
    overflowVisible && classes.overflowVisible,
  );

  return finalScrollTarget ? (
    <ReactInfiniteScroll
      {...props}
      className={className}
      key={scrollableKey}
      scrollableTarget={finalScrollTarget as unknown as ReactNode}
    >
      {children}
    </ReactInfiniteScroll>
  ) : (
    <ReactInfiniteScroll {...props} className={className}>
      {children}
    </ReactInfiniteScroll>
  );
};
